/**
 * ProviderDirectory — a searchable, filterable directory of the provider corpus.
 * Fetches the full catalogue (website/public/providers.json — thousands of
 * vendors) at runtime; until it loads (and for crawlers/no-JS) it renders the
 * seeded promoted vendor names as real text. Vendor cards show APIs, a type-safe
 * trust badge, and archetype tags — the substance + SEO + credibility.
 *
 * Two filter axes: industry vertical (from the catalogue's `vertical` field,
 * labelled by `verticalConfidence` = verified | inferred — never a source name)
 * and archetype (the data objects a vendor's APIs expose).
 *
 * Names only (nominative fair use); a trademark line sits at the foot.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '../card';
import { Badge } from '../badge';
import { Button } from '../button';
import { Input } from '../input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';
import { ContributionForm } from './ContributionForm';
import { cn } from '../../utils';

export interface ProviderDirectoryEntry {
  readonly key: string;
  readonly vendor: string;
  readonly apis: number;
  readonly fields: number;
  readonly mapped: number;
  readonly typeSafe: number;
  readonly domainEntities: number;
  readonly archetypes: readonly string[];
  readonly promoted: boolean;
  /** Industry vertical (e.g. "CRM & Sales"), when classified. */
  readonly vertical?: string;
  /** Confidence in the vertical classification — surfaced to the visitor as a
   *  trust signal. We never expose the underlying source (Rule: sources are not
   *  publicised), only whether the label is verified or inferred. */
  readonly verticalConfidence?: 'verified' | 'inferred';
  // ── Connector completeness + provenance (D.18 §2.3) — additive, computed into
  //    providers.json by the `providers-completeness` marketing generator
  //    (reuses scoreCompleteness). Static in the JSON, so they render on the
  //    bundled/sovereign catalogue too (no API needed for the badge).
  /** Protocol-aware completeness score 0–100 (best of the vendor's connectors). */
  readonly completenessScore?: number;
  /** Whether the score is below the completeness threshold → "needs improvement". */
  readonly needsImprovement?: boolean;
  /** How the connector entered the library (drives the "Improved by …" line). */
  readonly source?: 'system-inferred' | 'documentation' | 'openapi' | 'customer-contribution' | 'manual';
  /** Contributing party — display-only provenance, never an ownership grant. */
  readonly ownership?: {
    readonly contributorName?: string;
    readonly contributorEmail?: string;
    readonly promotedFromTenant?: string;
  };
}

type DirProvider = ProviderDirectoryEntry;

export interface ProviderDirectoryProps {
  /** Promoted vendor names, inlined for instant + crawlable render pre-fetch. */
  readonly seedNames: readonly string[];
  readonly totals?: { readonly vendors?: number; readonly apis?: number; readonly typeSafe?: number; readonly fields?: number; readonly mapped?: number; readonly promoted?: number };
  /**
   * Pre-bundled catalogue — when supplied, the directory renders it directly
   * and makes NO network request. This is the air-gapped / sovereign path
   * (Rule 12: zero egress); the data ships inside the build. Takes precedence
   * over `fetchUrl`.
   */
  readonly providers?: readonly ProviderDirectoryEntry[];
  /** Full catalogue JSON (served from public/) — the SaaS marketing-site path. */
  readonly fetchUrl?: string;
  /**
   * When set, each vendor card exposes a "Use this connector" action. Lets the
   * directory double as a picker (e.g. onboarding) without the primitive
   * knowing anything about routing.
   */
  readonly onSelect?: (provider: ProviderDirectoryEntry) => void;
  /** Label for the per-card select action. */
  readonly selectLabel?: string;
  /**
   * Absolute URL of the live connector-contribution route
   * (POST /v1/library/contributions). When SET, each card shows a "Help improve"
   * action that opens the contribution form pre-filled with the vendor. When
   * UNSET (the air-gapped / sovereign bundled site — no API), the form degrades
   * to a contact-us fallback; the completeness badge still renders (it's static
   * in providers.json), so the directory never depends on an API being present.
   */
  readonly contributionEndpoint?: string | undefined;
  /** Contact e-mail for the contribution fallback (single-sourced by the host). */
  readonly contactEmail?: string;
}

type SortKey = 'fields' | 'apis' | 'typeSafe' | 'vendor';
const RENDER_CAP = 240;

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  return n.toLocaleString('en-GB');
}

export function ProviderDirectory({ seedNames, totals, providers, fetchUrl = '/providers.json', onSelect, selectLabel = 'Use this connector', contributionEndpoint, contactEmail }: ProviderDirectoryProps) {
  const [all, setAll] = useState<DirProvider[] | null>(providers ? [...providers] : null);
  const [q, setQ] = useState('');
  const [promotedOnly, setPromotedOnly] = useState(false);
  const [sort, setSort] = useState<SortKey>('fields');
  const [archetype, setArchetype] = useState<string | null>(null);
  const [vertical, setVertical] = useState<string | null>(null);
  // "Help improve" → contribution dialog. We hold which vendor name to pre-fill;
  // a non-null value opens the form. The form is mounted once (not per-card).
  const [improveName, setImproveName] = useState<string | null>(null);

  // Deep-link into the contribution flow: /corpus?contribute=1 opens the dialog
  // with a blank vendor (the /submit-api page's CTA target). Read once on mount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (new URLSearchParams(window.location.search).get('contribute')) setImproveName('');
  }, []);

  // Archetypes present in the catalogue, most common first (for the filter chips).
  const archetypes = useMemo(() => {
    if (!all) return [];
    const counts = new Map<string, number>();
    for (const p of all) for (const a of p.archetypes) counts.set(a, (counts.get(a) ?? 0) + 1);
    return [...counts.entries()].sort((x, y) => y[1] - x[1]).slice(0, 14).map(([a]) => a);
  }, [all]);

  // Industry verticals present in the catalogue, most common first. Sourced from
  // the `vertical` field only (any classified confidence); the underlying source
  // is never exposed — just the label and, on each card, verified vs inferred.
  const verticals = useMemo(() => {
    if (!all) return [];
    const counts = new Map<string, number>();
    for (const p of all) {
      if (p.vertical) counts.set(p.vertical, (counts.get(p.vertical) ?? 0) + 1);
    }
    return [...counts.entries()].sort((x, y) => y[1] - x[1]).map(([v]) => v);
  }, [all]);

  useEffect(() => {
    // Bundled catalogue (sovereign / air-gapped): already in hand, never fetch.
    if (providers) { setAll([...providers]); return; }
    // During prerender, skip the fetch so the static HTML stays lean (just the
    // crawlable seed names) rather than baking hundreds of cards in.
    if (typeof window !== 'undefined' && (window as { __IX_PRERENDER__?: boolean }).__IX_PRERENDER__ === true) return;
    let cancelled = false;
    fetch(fetchUrl)
      .then((r) => r.json())
      .then((j: { providers?: DirProvider[] }) => { if (!cancelled) setAll(j.providers ?? []); })
      .catch(() => { if (!cancelled) setAll([]); });
    return () => { cancelled = true; };
  }, [providers, fetchUrl]);

  const rows = useMemo(() => {
    if (!all) return [];
    const t = q.trim().toLowerCase();
    let r = promotedOnly ? all.filter((p) => p.promoted) : all;
    if (vertical) r = r.filter((p) => p.vertical === vertical);
    if (archetype) r = r.filter((p) => p.archetypes.includes(archetype));
    if (t) r = r.filter((p) => p.vendor.toLowerCase().includes(t) || p.archetypes.some((a) => a.toLowerCase().includes(t)));
    r = [...r].sort((a, b) => (sort === 'vendor' ? a.vendor.localeCompare(b.vendor) : (b[sort] - a[sort])));
    return r;
  }, [all, q, promotedOnly, sort, archetype, vertical]);

  return (
    <div>
      {totals && (
        <p className="mb-(--space-grid) text-center font-mono text-sm uppercase tracking-wider text-muted-foreground">
          {totals.apis ? `${compact(totals.apis)} APIs` : ''}{totals.vendors ? ` · ${compact(totals.vendors)} vendors` : ''}{totals.typeSafe ? ` · ${compact(totals.typeSafe)} type-safe field mappings` : ''}
        </p>
      )}

      {/* Controls. Stack full-width on mobile (a clean column, no ragged wrap),
          revert to the inline wrapped row from sm up. */}
      <div className="mb-(--space-grid) flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search vendors or archetypes (e.g. Stripe, payment, person)…"
          className="w-full sm:min-w-60 sm:flex-1"
          aria-label="Search the provider corpus"
        />
        {verticals.length > 0 && (
          <Select value={vertical ?? '__all__'} onValueChange={(v) => setVertical(v === '__all__' ? null : v)}>
            <SelectTrigger className="w-full sm:w-52" aria-label="Filter by industry vertical"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All industries</SelectItem>
              {verticals.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
          <SelectTrigger className="w-full sm:w-44" aria-label="Sort vendors"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="fields">Most fields</SelectItem>
            <SelectItem value="apis">Most APIs</SelectItem>
            <SelectItem value="typeSafe">Most type-safe</SelectItem>
            <SelectItem value="vendor">A–Z</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => setPromotedOnly((v) => !v)}
          className={cn('w-full rounded-full border px-3 py-1.5 text-sm transition-colors sm:w-auto', promotedOnly ? 'border-primary bg-primary-light text-primary' : 'border-border text-muted-foreground hover:text-foreground')}
        >
          Ready connectors only
        </button>
      </div>

      {/* Archetype filter — browse by the data objects a vendor's APIs expose. */}
      {archetypes.length > 0 && (
        <div className="mb-(--space-grid) flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setArchetype(null)}
            className={cn('rounded-full border px-3 py-1 text-xs capitalize transition-colors', archetype === null ? 'border-primary bg-primary-light text-primary' : 'border-border text-muted-foreground hover:text-foreground')}
          >
            All
          </button>
          {archetypes.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setArchetype((cur) => (cur === a ? null : a))}
              className={cn('rounded-full border px-3 py-1 text-xs capitalize transition-colors', archetype === a ? 'border-primary bg-primary-light text-primary' : 'border-border text-muted-foreground hover:text-foreground')}
            >
              {a.replace(/-/g, ' ')}
            </button>
          ))}
        </div>
      )}

      {all === null ? (
        // Pre-fetch / no-JS: crawlable seed of the ready connectors.
        <div>
          <p className="mb-3 text-sm text-muted-foreground">Loading the full directory… ready connectors:</p>
          <ul className="flex flex-wrap gap-2">
            {seedNames.map((name, i) => (
              <li key={i} className="inline-flex items-center rounded-full border border-border px-3 py-1 text-sm text-muted-foreground">{name}</li>
            ))}
          </ul>
        </div>
      ) : (
        <>
          <p className="mb-(--space-grid) text-sm text-muted-foreground">
            {rows.length.toLocaleString('en-GB')} {promotedOnly ? 'ready' : 'matching'} vendors{rows.length > RENDER_CAP ? ` (showing ${RENDER_CAP})` : ''}
          </p>
          <div className="grid grid-cols-1 gap-(--space-grid) sm:grid-cols-2 lg:grid-cols-3">
            {rows.slice(0, RENDER_CAP).map((p) => {
              const pct = p.fields > 0 ? Math.round((p.typeSafe / p.fields) * 100) : 0;
              return (
                <Card key={p.key} className="motion-lift p-(--space-card)">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-base font-semibold text-foreground">{p.vendor}</h3>
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                      {/* Completeness — a per-card quality signal. Static in providers.json,
                          so it renders on the bundled/sovereign catalogue too. */}
                      {typeof p.completenessScore === 'number' && (
                        <Badge variant={p.needsImprovement ? 'warning' : 'success'} title={`Connector completeness: ${p.completenessScore}/100`}>
                          {p.needsImprovement ? 'Needs improvement' : `${p.completenessScore}/100`}
                        </Badge>
                      )}
                      {p.promoted && <Badge variant="secondary">Ready</Badge>}
                    </div>
                  </div>
                  <p className="mt-1 font-mono text-xs text-muted-foreground">
                    {p.apis} API{p.apis === 1 ? '' : 's'} · {compact(p.fields)} fields{pct ? ` · ${pct}% type-safe` : ''}
                  </p>
                  {p.vertical && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {p.vertical}{p.verticalConfidence ? ` · ${p.verticalConfidence}` : ''}
                    </p>
                  )}
                  {/* Contributor attribution — only for an actual customer-contribution with a
                      named contributor. Display-only provenance, never an ownership claim. */}
                  {p.source === 'customer-contribution' && p.ownership?.contributorName && (
                    <p className="mt-1 text-xs text-primary">Improved by {p.ownership.contributorName}</p>
                  )}
                  {p.archetypes.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p.archetypes.slice(0, 4).map((a) => (
                        <span key={a} className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{a}</span>
                      ))}
                    </div>
                  )}
                  {/* Help improve — opens the contribution form pre-filled with this vendor.
                      Always offered (community can improve any connector); the form itself
                      degrades to contact-us when there's no contribution API. */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setImproveName(p.vendor)}
                    className="mt-(--space-card) w-full"
                  >
                    Help improve
                  </Button>
                  {onSelect && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onSelect(p)}
                      className="mt-2 w-full"
                    >
                      {selectLabel}
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
          {rows.length === 0 && <p className="text-center text-muted-foreground">No vendors match “{q}”.</p>}
        </>
      )}

      <p className="mt-(--space-card) text-center text-xs text-muted-foreground/70">
        All product names are trademarks of their respective owners, listed to show interoperability — not endorsement.
      </p>

      {/* One contribution dialog for the whole directory, driven by `improveName`.
          Pre-fills the vendor a card's "Help improve" opened. The form gates its
          own submit on `contributionEndpoint` (degrades to contact-us air-gapped). */}
      <ContributionForm
        open={improveName !== null}
        onOpenChange={(o) => { if (!o) setImproveName(null); }}
        endpoint={contributionEndpoint}
        initialDisplayName={improveName ?? ''}
        {...(contactEmail ? { contactEmail } : {})}
      />
    </div>
  );
}
